import { MultiSelectFieldOption } from "../../beta/components/MultiSelectField/types";
import { CheckState } from "../../types";
import { CSSProperties, forwardRef, useCallback, useMemo } from "react";
import { OptionsPanel, OptionsPanelHandle } from "./OptionsPanel";
import { VirtualizedOptionsPanel } from "./VirtualizedOptionsPanel";
import {
  GetItemPropsFn,
  GetMenuPropsFn,
  SectionMeta,
  SelectItem,
} from "../types/selectFieldInternalTypes";

export type MultiSelectOptionsProps = {
  isOpen: boolean;
  items: SelectItem[];
  getMenuProps: GetMenuPropsFn;
  getItemProps: GetItemPropsFn;
  highlightedIndex: number;
  selectedOptions: MultiSelectFieldOption[];
  hasMore: boolean;
  onLoadMore: () => void;
  loading: boolean;
  loadingMore: boolean;
  disableAutoLoadMore?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  sectionsMeta?: SectionMeta[];
  virtualize?: boolean;
};

export const MultiSelectOptions = forwardRef<
  OptionsPanelHandle,
  MultiSelectOptionsProps
>(({ selectedOptions, items, virtualize, ...rest }, ref) => {
  const selectedIds = useMemo(
    () => new Set(selectedOptions.map((opt) => opt.id)),
    [selectedOptions],
  );

  const getCheckedState = useCallback(
    (item: SelectItem): CheckState => {
      if (item.type === "select-all" || item.type === "select-filtered") {
        return item.checkState ?? "indeterminate";
      }
      const checkId =
        item.type === "pinned-option" ? item.original.id : item.id;
      return selectedIds.has(checkId) ? "checked" : "unchecked";
    },
    [selectedIds],
  );

  const ListComponent = virtualize ? VirtualizedOptionsPanel : OptionsPanel;

  return (
    <ListComponent
      {...rest}
      ref={ref}
      items={items}
      selectionType="multiple"
      getCheckedState={getCheckedState}
    />
  );
});

MultiSelectOptions.displayName = "MultiSelectOptions";
